// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/export-styles */
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@werner/App/Components/Common/XngZone";
import { CustomAppState } from "@werner/App/Store/Reducers";
import { layoutContainer, xScreen } from "@werner/XDL/Utilities";
import * as cssLinter from "css";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    rowCount = "rowCount",
    gap = "gap",
    isFullWidth = "isFullWidth",
    customCSS = "customCSS",
    // XNg-Changes
    mobileGap = "mobileGap",
    margin = "margin",
    overrideStyle = "overrideStyle",
    itemsList = "itemsList",
}

const StyledWrapperX = getStyledWrapper("div") as any; // typeof StyledWrapper & { sticky: boolean };

interface Props extends WidgetProps {
    fields: {
        [fields.rowCount]: number;
        [fields.gap]: number;
        [fields.isFullWidth]: boolean;
        [fields.customCSS]: string;
        [fields.mobileGap]: number;
        [fields.margin]: string;
        [fields.overrideStyle]: boolean;
        [fields.itemsList]: {
            fields: {
                name: string;
                fullWidth: boolean;
                mobileFullWidth: boolean;
                addSeparate: boolean;
            };
        }[];
    };
}

const styles: { container?: InjectableCss; itemContainer?: InjectableCss } = {
    container: {
        css: css`
            max-width: 100%;
            ${({ margin }: any) => margin && `margin: ${margin};`}
            > div {
                margin-bottom: ${({ gap }: any) => gap}px;
                ${xScreen("lte", "md")} {
                    margin-bottom: ${({ gap }: any) => gap}px;
                }
                &:last-child {
                    margin-bottom: 0;
                }
            }
        `,
    },
    itemContainer: {
        css: css`
            width: 100%;
            ${layoutContainer}
        `,
    },
};

// A widget that makes a configurable number of row zones within its container.
const Rows = ({
    fields: {
        margin,
        rowCount,
        gap,
        // isFullWidth,
        customCSS,
        overrideStyle,
        itemsList,
        mobileGap,
    },
    id,
}: Props) => {
    const rows: JSX.Element[] = [];
    const isMobileScreen = useAppSelector(state => (state as CustomAppState).xCommon.isMobileScreen);

    if (overrideStyle) {
        itemsList?.forEach((item, index) => {
            const { fullWidth: isFullWidthItem, mobileFullWidth, addSeparate, name: itemName = "" } = item.fields;
            const name = itemName.replace(/\s/g, "");
            // "full-width" class for "LayoutContainer" style
            const className = [
                `${isFullWidthItem ? "full-width" : ""}`,
                `${mobileFullWidth && isMobileScreen ? "mobile-full-width" : ""}`,
            ].join(" ");

            const itemProps = {
                addSeparate,
            };

            rows.push(
                // eslint-disable-next-line react/no-array-index-key
                <StyledWrapper
                    key={`StyledWrapper-${id}-${name || index}`}
                    className={className}
                    {...itemProps}
                    {...styles.itemContainer}
                >
                    <XngZone contentId={id} zoneName={`Content_${name || index}`} hint={`Zone for ${itemName}`} />
                </StyledWrapper>,
            );
        });
    } else {
        for (let row = 0; row < rowCount; row = row + 1) {
            rows.push(
                <GridItem key={`GridItem-${id}-${row}`} width={[12, 12, 12, 12, 12]}>
                    <Zone contentId={id} zoneName={`Content${row}`} />
                </GridItem>,
            );
        }
    }

    if (overrideStyle) {
        return (
            <StyledWrapperX gap={gap} margin={margin} mobileGap={mobileGap} {...styles.container}>
                {rows}
            </StyledWrapperX>
        );
    }

    const gridCss = css`
        ${customCSS}
    `;

    return (
        <GridContainer
            gap={gap}
            css={css`
                margin: 0 auto;
                ${gridCss}
            `}
        >
            {rows}
        </GridContainer>
    );
};

const defaultCustomCss = `& {
}`;
const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: Rows,
    definition: {
        group: "Basic",
        icon: "distribute-spacing-vertical",
        fieldDefinitions: [
            {
                name: fields.rowCount,
                displayName: "Row Count",
                editorTemplate: "IntegerField",
                defaultValue: 4,
                fieldType: "General",
                isVisible: item => !item.fields.overrideStyle,
            },
            {
                name: fields.gap,
                displayName: "Gap Between Items",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                sortOrder: 2,
            },
            // {
            //     name: fields.isFullWidth,
            //     displayName: "Full Width",
            //     editorTemplate: "CheckboxField",
            //     defaultValue: false,
            //     fieldType: "General",
            //     isVisible: (_item, _advancedFeaturesEnabled, theme) => !!theme?.content?.isFullWidth,
            //     sortOrder: 3,
            // },
            {
                name: "customCSS",
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }
                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }
                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            {
                name: "mobileGap",
                displayName: "Gap Between Items on Mobile",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
            },
            {
                name: "margin",
                displayName: "Margin Size",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "0 auto",
                placeholder: "Enter: 5px, 5px auto, ...etc",
                validate: (value: string | null, item: HasFields) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("margin", value) ? null : `Invalid Input`;
                },
            },
            {
                name: "overrideStyle",
                displayName: "Override Style",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: "itemsList",
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.name || "Zone";
                },
                defaultValue: [],
                fieldType: "General",
                isVisible: item => item.fields.overrideStyle,
                fieldDefinitions: [
                    {
                        name: "name",
                        displayName: "Zone Name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        placeholder: "Zone Name",
                        isRequired: true,
                    },
                    {
                        name: "fullWidth",
                        displayName: "Full Width",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "mobileFullWidth",
                        displayName: "Mobile Full Width",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "addSeparate",
                        displayName: "Add Separate",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
